import { checkContrast } from './contrast'

export {
  CONTRAST_RULES,
  PRODUCT_PALETTE,
  checkContrast,
  contrastRatio,
  parseColor,
  readableOnAccent,
  relativeLuminance,
  resolveTenantPalette,
  type ContrastViolation,
  type Rgb,
} from './contrast'

/**
 * What a tenant may brand, and what counts as a colour.
 *
 * This lives in core rather than in `@flowza/ui` because it is a validation rule, not a rendering
 * concern, and two callers need it: the browser, which applies the tokens, and the API, which
 * decides whether to STORE them. `@flowza/ui` re-exports it so the design system keeps a single
 * import surface and `packages/ui/test/tokens.test.ts` keeps holding this list to the CSS.
 *
 * The API cannot simply import the UI package — a Worker must not pull in React to validate a hex
 * string — and duplicating the list would let the two drift, which for a security-shaped
 * allowlist means the server eventually accepts something the client would have refused.
 *
 * BUSINESS LOGIC LIVES IN packages/core (CLAUDE.md §4). This is business logic: it decides what a
 * customer is allowed to store.
 */

/**
 * The semantic tokens a tenant may override. Branding overrides the SEMANTIC layer only — never a
 * primitive — which is what makes white-label a variable swap rather than a fork (ux.md §1).
 *
 * Held to `packages/ui/src/styles/theme.css` by `packages/ui/test/tokens.test.ts`, which fails if
 * either side gains a token the other lacks.
 */
export const BRANDABLE_TOKENS = [
  'surface-base',
  'surface-raised',
  'surface-sunken',
  'border-subtle',
  'border-strong',
  'text-primary',
  'text-muted',
  'text-on-accent',
  'accent',
  'accent-hover',
  'focus-ring',
  'success',
  'warning',
  'danger',
] as const

export type BrandableToken = (typeof BRANDABLE_TOKENS)[number]

const BRANDABLE = new Set<string>(BRANDABLE_TOKENS)

/** `--accent` and `accent` are both accepted; the leading `--` is punctuation, not identity. */
export function normaliseTokenName(name: string): string {
  return name.startsWith('--') ? name.slice(2) : name
}

export function isBrandableToken(name: string): name is BrandableToken {
  return BRANDABLE.has(normaliseTokenName(name))
}

/* -------------------------------------------------------------------------- value grammar --- */

/** #rgb, #rgba, #rrggbb, #rrggbbaa. */
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

/**
 * A colour function with strictly numeric arguments: rgb, rgba, hsl, hsla, oklch, oklab.
 * The argument list may hold digits, signs, decimal points, `%`, `deg`, `none`, separators and
 * the `/` alpha delimiter — and nothing else. No identifiers, so no nested `var()` or `url()`;
 * no quotes, no backslashes, no comment sequences.
 */
const COLOR_FN = /^(rgba?|hsla?|oklch|oklab)\(\s*([0-9a-z%.,/+\- ]*)\)$/i
const FN_ARG = /^(?:[-+]?(?:\d+\.?\d*|\.\d+)(?:%|deg|rad|turn|grad)?|none)$/i

function isColorFunction(value: string): boolean {
  const match = COLOR_FN.exec(value)
  if (!match) return false
  const args = match[2]
  if (args === undefined) return false
  const parts = args.split(/[\s,/]+/).filter((p) => p.length > 0)
  if (parts.length < 3 || parts.length > 4) return false
  return parts.every((p) => FN_ARG.test(p))
}

/**
 * `transparent` and `currentColor` are the only bare keywords allowed. Named CSS colours are
 * deliberately excluded: they add 148 spellings to the parser for no capability a hex lacks.
 */
const KEYWORDS = new Set(['transparent', 'currentcolor'])

/* --------------------------------------------------------------------------- serving out --- */

export type ServedBranding = {
  /** Only tokens that passed BOTH checks. Empty means the ceremony renders unbranded. */
  readonly tokens: Readonly<Record<string, string>>
  /** Token names that were stored but refused on the way out — for a log line, never for a caller. */
  readonly rejected: readonly string[]
}

/**
 * Narrow a stored branding row to what may be served to a SIGNER.
 *
 * Re-validated on the way OUT even though `PUT /v1/organization/branding` validated on the way in,
 * because the two are different questions asked at different times. A row can predate a tightening
 * of the grammar, or arrive from a data migration, or from staff tooling — and the one surface where
 * a bad value lands is a stranger's browser on the page whose whole trust story is that no third
 * party puts anything on it. Validating once, at write, would make that assumption load-bearing
 * forever.
 *
 * `primary_color` folds into `accent` only when `theme_tokens` does not already set it. The two
 * columns exist because an EMAIL renderer has no CSS custom properties to cascade and reads
 * `primary_color` directly; on this path they are one value and the explicit token wins.
 */
export function serveCeremonyBranding(row: {
  readonly primary_color: string | null
  readonly theme_tokens: Readonly<Record<string, unknown>> | null
}): ServedBranding {
  const tokens: Record<string, string> = {}
  const rejected: string[] = []

  for (const [rawName, rawValue] of Object.entries(row.theme_tokens ?? {})) {
    const name = normaliseTokenName(rawName)
    if (!isBrandableToken(name) || typeof rawValue !== 'string' || !isBrandableColor(rawValue)) {
      rejected.push(name)
      continue
    }
    tokens[name] = rawValue.trim()
  }

  if (tokens.accent === undefined && row.primary_color !== null) {
    if (isBrandableColor(row.primary_color)) tokens.accent = row.primary_color.trim()
    else rejected.push('primary_color')
  }

  // THE CONTRAST FLOOR, AGAIN, ON THE WAY OUT.
  //
  // `PUT /v1/organization/branding` refuses a palette that would hide the consent wording or the
  // Decline affordance — but only from the moment that rule existed. A row stored before it, or by
  // a data migration, or by staff tooling, is still out there, and the one surface it reaches is a
  // stranger's browser on the page whose entire trust story is that they can refuse.
  //
  // So a pair that fails is DROPPED here, both halves of it, and the ceremony renders those two
  // tokens in the product defaults — which clear the floor by construction. Dropping one half would
  // be worse than dropping neither: it would leave a tenant's colour paired against a default
  // nobody checked that combination for.
  //
  // Not an error, and not a refusal of the whole profile: the rest of the branding still applies.
  // The names go into `rejected`, which the API logs, so a tenant whose colour is being ignored can
  // be told why.
  const failures = checkContrast(tokens)
  for (const failure of failures) {
    for (const token of [failure.foreground, failure.background]) {
      if (token in tokens) {
        delete tokens[token]
        if (!rejected.includes(token)) rejected.push(token)
      }
    }
  }

  return { tokens, rejected }
}

export function isBrandableColor(value: string): boolean {
  const v = value.trim()
  // Length cap before any pattern work: a pathological value should be cheap to reject.
  if (v.length === 0 || v.length > 64) return false
  // Characters that are never legal in any accepted form. Parens are absent because the colour
  // function grammar anchors them to exactly two positions and permits no identifier between.
  if (/[;{}<>"'\\]/.test(v)) return false
  if (KEYWORDS.has(v.toLowerCase())) return true
  if (HEX.test(v)) return true
  return isColorFunction(v)
}

/* ------------------------------------------------------------------------- the email lane --- */

/** The product's own accent, for a tenant with no branding or an unpaintable colour. */
export const PRODUCT_ACCENT_HEX = '#2f5fd8'

const HEX_SHORT = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i
const HEX_LONG = /^#[0-9a-f]{6}$/i
const RGB_FN = /^rgba?\(\s*([0-9a-z%.,/+\- ]*)\)$/i

/**
 * The colour an EMAIL may be painted in — a stricter question than `isBrandableColor`.
 *
 * A browser gets `oklch()`, `color-mix()` and eight-digit hex. An email client is Outlook's Word
 * rendering engine, a ten-year-old Android client, and Gmail's HTML sanitiser, and what they do
 * with a colour they cannot parse is DROP THE DECLARATION. On a `<td>` that is the call-to-action
 * button, dropping it leaves white text on white — the recipient sees no button at all, on the one
 * message whose entire job is to be clicked. That failure is silent on our side and total on
 * theirs, which is why this narrows rather than trusting the wider grammar.
 *
 * So: normalise to SIX-DIGIT HEX, which every client since the 1990s paints, or return null and let
 * the caller fall back to the product accent. Accepted on the way in are `#rgb`, `#rrggbb` and
 * `rgb()`/`rgba()` with integer or percentage channels.
 *
 * Deliberately refused, each because the safe reading is "we cannot paint this":
 *   - `oklch()` / `oklab()` — no email client support worth relying on.
 *   - eight-digit hex and `rgba()` below full alpha — a translucent button over an unknown client
 *     background is a button whose contrast we cannot predict, and `#00000000` is an invisible one.
 *   - `transparent` and `currentColor` — invisible by construction.
 *   - `hsl()` — paintable in most clients, but the conversion is arithmetic this module would carry
 *     for a form no colour picker in the product emits. Falls back rather than guesses.
 */
export function emailSafeColor(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const v = value.trim().toLowerCase()
  if (v.length === 0 || v.length > 64) return null
  // The wider grammar first: anything it refuses must never reach a mail body either.
  if (!isBrandableColor(v)) return null

  if (HEX_LONG.test(v)) return v
  const short = HEX_SHORT.exec(v)
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`

  const fn = RGB_FN.exec(v)
  if (fn === null) return null
  const args = (fn[1] ?? '').split(/[\s,/]+/).filter((p) => p.length > 0)
  if (args.length === 4) {
    // An alpha that is not fully opaque. Composite over what? The client's background is unknown.
    const alpha = args[3]
    if (alpha === undefined || !/^(1|1\.0*|100%)$/.test(alpha)) return null
  } else if (args.length !== 3) return null

  const channels: number[] = []
  for (const raw of args.slice(0, 3)) {
    const pct = raw.endsWith('%')
    const n = Number(pct ? raw.slice(0, -1) : raw)
    if (!Number.isFinite(n)) return null
    const byte = Math.round(pct ? (n / 100) * 255 : n)
    if (byte < 0 || byte > 255) return null
    channels.push(byte)
  }
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

/**
 * The ONE colour an email is painted in, resolved from a branding row.
 *
 * Never null: an email always has a button, so this always answers with something paintable. A
 * tenant whose stored colour cannot be painted gets the product accent rather than a broken one,
 * and the caller logs the substitution — silently sending an unbranded mail to a paying tenant is
 * the kind of defect nobody reports because it looks like it worked.
 */
export function emailBrandColor(row: {
  readonly primary_color?: string | null
  readonly theme_tokens?: Readonly<Record<string, unknown>> | null
}): { readonly hex: string; readonly branded: boolean } {
  const token = row.theme_tokens?.accent
  const candidates = [typeof token === 'string' ? token : null, row.primary_color ?? null]
  for (const candidate of candidates) {
    if (candidate === null) continue
    const hex = emailSafeColor(candidate)
    if (hex !== null) return { hex, branded: true }
  }
  return { hex: PRODUCT_ACCENT_HEX, branded: false }
}
